import { theme, type ThemeConfig } from 'antd';
import { getTokens, typography, layout, type ThemeTokens } from './tokens.ts';
import type { ThemeMode } from '../stores/themeStore.ts';

export function buildModuleTheme(mode: ThemeMode): ThemeConfig {
  const tokens = getTokens(mode);
  const dark = mode === 'dark';
  const text = dark ? '#f5f5f5' : '#212121';
  const surface = dark ? '#1e1e1e' : '#ffffff';
  const subtle = dark ? '#292929' : '#f5f5f5';
  const border = dark ? '#424242' : '#e0e0e0';

  return {
    token: {
      fontFamily: typography.fontFamily, fontSize: 14, fontWeightStrong: 500,
      lineHeight: 1.43, borderRadius: 4, borderRadiusLG: 4, controlHeight: 36,
      colorPrimary: tokens.colorPrimary, colorPrimaryHover: tokens.colorPrimaryHover,
      colorLink: tokens.colorLink, colorLinkHover: tokens.colorLinkHover, colorText: text,
      colorTextSecondary: dark ? '#bdbdbd' : '#616161',
      colorTextDisabled: dark ? '#9e9e9e' : '#757575',
      colorBgContainer: surface, colorBgElevated: surface,
      colorBorder: dark ? '#616161' : '#bdbdbd', colorBorderSecondary: border,
    },
    components: {
      Button: { colorPrimary: tokens.colorPrimary, colorPrimaryHover: tokens.colorPrimaryHover,
        colorPrimaryActive: tokens.colorPrimaryActive,
        primaryColor: tokens.colorPrimaryForeground, borderRadius: 4,
        paddingInline: 16, fontWeight: 500 },
      Input: { activeBorderColor: tokens.colorLink, hoverBorderColor: tokens.colorLinkHover, borderRadius: 4 },
      Select: { borderRadius: 4, optionSelectedBg: tokens.colorPrimaryBg },
      Table: { headerColor: text, headerBg: subtle, borderColor: border,
        rowHoverBg: tokens.tableRowHoverBg, cellFontSize: 14,
        cellPaddingBlock: 12, cellPaddingInline: 16 },
      Tabs: { itemSelectedColor: tokens.colorLink, itemHoverColor: tokens.colorLinkHover,
        itemActiveColor: tokens.colorLink, inkBarColor: tokens.colorLink, titleFontSize: 14 },
      Form: { labelFontSize: 12, itemMarginBottom: 20 },
      Card: { headerFontSize: 18, headerFontSizeSM: 18, paddingLG: 20 },
      Modal: { borderRadiusLG: 4, titleFontSize: 20 },
      Checkbox: { colorPrimary: tokens.colorPrimary, colorPrimaryHover: tokens.colorPrimaryHover },
      Switch: { colorPrimary: tokens.colorPrimary, colorPrimaryHover: tokens.colorPrimaryHover },
      Radio: { colorPrimary: tokens.colorPrimary },
      Pagination: { itemActiveBg: tokens.colorPrimaryBg,
        colorPrimary: tokens.colorLink, colorPrimaryHover: tokens.colorLinkHover },
    },
  };
}

/**
 * Builds a complete Ant Design 5 ThemeConfig from our shared application
 * tokens + the appropriate algorithm (default or darkAlgorithm).
 */
export function buildAntdTheme(mode: ThemeMode): ThemeConfig {
  const t: ThemeTokens = getTokens(mode);

  return {
    algorithm: mode === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,

    token: {
      // ── Color ──────────────────────────────────────
      colorPrimary: t.colorPrimary,
      colorPrimaryHover: t.colorPrimaryHover,
      colorPrimaryActive: t.colorPrimaryActive,
      colorPrimaryBg: t.colorPrimaryBg,
      colorPrimaryBgHover: t.colorPrimaryBg,
      colorPrimaryText: t.colorLink,
      colorPrimaryTextHover: t.colorLinkHover,
      colorPrimaryTextActive: t.colorLink,
      colorLink: t.colorLink,
      colorLinkHover: t.colorLinkHover,
      colorLinkActive: t.colorLink,
      controlOutline: t.colorPrimaryBg,
      colorSuccess: t.colorSuccess,
      colorWarning: t.colorWarning,
      colorError: t.colorError,
      colorInfo: t.colorInfo,
      colorText: t.colorText,
      colorTextSecondary: t.colorTextSecondary,
      colorTextTertiary: t.colorTextTertiary,
      colorTextQuaternary: t.colorTextQuaternary,
      colorBgContainer: t.colorBgContainer,
      colorBgLayout: t.colorBgLayout,
      colorBgElevated: t.colorBgElevated,
      colorBorder: t.colorBorder,
      colorBorderSecondary: t.colorBorderSecondary,

      // ── Typography ─────────────────────────────────
      fontFamily: typography.fontFamily,
      fontSize: typography.fontSizeMd,
      fontSizeSM: typography.fontSizeSm,
      fontSizeLG: typography.fontSizeLg,
      fontSizeXL: typography.fontSizeXl,
      fontSizeHeading1: typography.fontSizeH1,
      fontSizeHeading2: typography.fontSizeH2,
      fontSizeHeading3: typography.fontSizeH3,
      fontSizeHeading4: typography.fontSizeLg,
      fontSizeHeading5: typography.fontSizeMd,
      fontWeightStrong: typography.fontWeightSemiBold,
      lineHeight: typography.lineHeight,

      // ── Shape ──────────────────────────────────────
      borderRadius: layout.borderRadius,
      borderRadiusSM: layout.borderRadiusSm,
      borderRadiusLG: layout.borderRadiusLg,
      borderRadiusXS: 2,

      // ── Shadows ────────────────────────────────────
      boxShadow: t.shadowSm,
      boxShadowSecondary: t.shadowMd,

      // ── Controls ───────────────────────────────────
      controlHeight: 36,
      controlHeightSM: 28,
      controlHeightLG: 44,

      // ── Motion ─────────────────────────────────────
      motionDurationFast: '0.15s',
      motionDurationMid: '0.25s',
      motionDurationSlow: '0.35s',
    },

    components: {
      // ── Layout ─────────────────────────────────
      Layout: {
        headerBg: t.headerBg,
        headerHeight: layout.headerHeight,
        bodyBg: t.colorBgLayout,
        siderBg: t.sidebarBg,
      },

      // ── Button ─────────────────────────────────
      Button: {
        colorPrimary: t.colorPrimary,
        colorPrimaryHover: t.colorPrimaryHover,
        colorPrimaryActive: t.colorPrimaryActive,
        colorLink: t.colorLink,
        colorLinkHover: t.colorLinkHover,
        colorLinkActive: t.colorLink,
        primaryColor: t.colorPrimaryForeground,
        primaryShadow: 'none',
        defaultBorderColor: t.colorBorder,
        defaultColor: t.colorText,
        defaultHoverColor: t.colorLink,
        defaultHoverBorderColor: t.colorLink,
        defaultActiveColor: t.colorLinkHover,
        defaultActiveBorderColor: t.colorLinkHover,
        borderRadius: layout.borderRadius,
        controlHeight: 36,
        contentFontSize: 14,
        contentLineHeight: 18 / 14,
        fontWeight: typography.fontWeightMedium,
        paddingInline: 20,
      },

      // ── Table ──────────────────────────────────
      Table: {
        headerBg: t.tableHeaderBg,
        headerColor: t.tableHeaderColor,
        headerSortActiveBg: t.tableHeaderBg,
        headerSortHoverBg: t.tableHeaderBg,
        headerFilterHoverBg: t.tableHeaderBg,
        headerSplitColor: t.tableBorder,
        rowHoverBg: t.tableRowHoverBg,
        rowSelectedBg: t.tableRowSelectedBg,
        rowSelectedHoverBg: t.tableRowSelectedBg,
        borderColor: t.tableBorder,
        cellPaddingBlock: 12,
        cellPaddingInline: 16,
        cellFontSize: 13,
        headerBorderRadius: 0,
        fontWeightStrong: typography.fontWeightSemiBold,
      },

      // ── Card ───────────────────────────────────
      Card: {
        headerBg: 'transparent',
        headerFontSize: 16,
        headerFontSizeSM: 14,
        borderRadiusLG: layout.borderRadiusLg,
        paddingLG: 24,
      },

      // ── Form ───────────────────────────────────
      Form: {
        labelFontSize: 13,
        labelColor: t.colorTextSecondary,
        labelRequiredMarkColor: t.colorError,
        verticalLabelPadding: '0 0 6px',
        itemMarginBottom: 20,
      },

      // ── Input ──────────────────────────────────
      Input: {
        activeBorderColor: t.colorLink,
        hoverBorderColor: t.colorLinkHover,
        activeShadow: `0 0 0 2px ${t.colorPrimaryBg}`,
        borderRadius: layout.borderRadius,
        paddingInline: 12,
        colorBorder: t.colorBorder,
        colorBgContainer: t.colorBgContainer,
      },

      // ── Select ─────────────────────────────────
      Select: {
        optionSelectedBg: t.colorPrimaryBg,
        optionActiveBg: t.colorPrimaryBg,
        selectorBg: t.colorBgContainer,
        borderRadius: layout.borderRadius,
      },

      // ── DatePicker ─────────────────────────────
      DatePicker: {
        activeBorderColor: t.colorLink,
        hoverBorderColor: t.colorLinkHover,
        activeShadow: `0 0 0 2px ${t.colorPrimaryBg}`,
      },

      // ── Tabs ───────────────────────────────────
      Tabs: {
        inkBarColor: t.colorLink,
        itemActiveColor: t.colorLink,
        itemHoverColor: t.colorLinkHover,
        itemSelectedColor: t.colorLink,
        titleFontSize: 14,
        titleFontSizeLG: 16,
      },

      // ── Tag ────────────────────────────────────
      Tag: {
        borderRadiusSM: layout.borderRadiusSm,
        defaultBg: t.colorBgLayout,
        defaultColor: t.colorTextSecondary,
      },

      // ── Modal ──────────────────────────────────
      Modal: {
        headerBg: t.colorBgElevated,
        titleFontSize: 18,
        borderRadiusLG: 12,
        paddingContentHorizontalLG: 28,
      },

      // ── Breadcrumb ─────────────────────────────
      Breadcrumb: {
        lastItemColor: t.colorText,
        itemColor: t.colorTextTertiary,
        linkColor: t.colorLink,
        linkHoverColor: t.colorLinkHover,
        separatorColor: t.colorTextQuaternary,
        fontSize: 13,
      },

      // ── Pagination ─────────────────────────────
      Pagination: {
        itemActiveBg: t.colorPrimaryBg,
        colorPrimary: t.colorLink,
        colorPrimaryHover: t.colorLinkHover,
        borderRadius: layout.borderRadius,
        itemSize: 32,
      },

      // ── Collapse ───────────────────────────────
      Collapse: {
        headerBg: t.colorBgLayout,
        contentBg: t.colorBgContainer,
        borderRadiusLG: layout.borderRadiusLg,
        headerPadding: '10px 16px',
        contentPadding: '16px',
      },

      // ── Tooltip ────────────────────────────────
      Tooltip: {
        colorBgSpotlight: t.colorBgSpotlight,
        borderRadius: layout.borderRadius,
        fontSize: 12,
      },

      // ── Switch / Checkbox / Radio ──────────────
      Switch: {
        colorPrimary: t.colorPrimary,
        colorPrimaryHover: t.colorPrimaryHover,
      },
      Checkbox: {
        colorPrimary: t.colorPrimary,
        colorPrimaryHover: t.colorPrimaryHover,
        borderRadiusSM: 3,
      },
      Radio: {
        colorPrimary: t.colorPrimary,
        dotSize: 8,
      },

      // ── Dropdown ───────────────────────────────
      Dropdown: {
        borderRadiusLG: layout.borderRadiusLg,
        controlItemBgHover: t.colorBgLayout,
        paddingBlock: 6,
      },

      // ── Divider ────────────────────────────────
      Divider: {
        colorSplit: t.colorBorderSecondary,
      },

      // ── Alert ──────────────────────────────────
      Alert: {
        borderRadiusLG: layout.borderRadiusLg,
      },

      // ── Spin ───────────────────────────────────
      Spin: {
        colorPrimary: t.colorPrimary,
      },

      // ── Steps ──────────────────────────────────
      Steps: {
        colorPrimary: t.colorPrimary,
      },

      // ── Progress ───────────────────────────────
      Progress: {
        defaultColor: t.colorPrimary,
        remainingColor: t.colorBorder,
      },

      // ── Statistic ──────────────────────────────
      Statistic: {
        titleFontSize: 13,
        contentFontSize: 28,
      },

      // ── Menu ───────────────────────────────────
      Menu: {
        itemColor: t.sidebarTextColor,
        itemHoverColor: t.colorLink,
        itemHoverBg: t.sidebarHoverBg,
        itemSelectedBg: t.sidebarSelectedBg,
        itemSelectedColor: t.sidebarSelectedColor,
        subMenuItemSelectedColor: t.sidebarSelectedColor,
        darkItemBg: t.sidebarBg,
        darkItemSelectedBg: t.sidebarSelectedBg,
        darkItemSelectedColor: t.sidebarSelectedColor,
        itemBorderRadius: layout.borderRadiusLg,
        subMenuItemBorderRadius: layout.borderRadius,
        itemMarginInline: 0,
        itemMarginBlock: 1,
        itemPaddingInline: 12,
        iconSize: 16,
        collapsedIconSize: 18,
        groupTitleFontSize: 11,
      },

      // ── Notification ───────────────────────────
      Notification: {
        width: 380,
      },
    },
  };
}
